import React from 'react';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  UserIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  EyeIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

export default function CurrentTaskHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-cyan-300 via-blue-100 to-white w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ClipboardDocumentListIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Admin Tasks Management</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive admin task management system for handling outstanding appraisals,
            employee feedback collection, and performance review workflows.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-6">
            <div className="flex items-center">
              <ClipboardDocumentListIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Admin Tasks Management Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-200">
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Outstanding Tasks</h3>
                <p className="text-cyan-700 text-sm leading-relaxed">
                  Track and manage all pending administrative tasks including
                  appraisals, reviews, and employee feedback requests.
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Appraisal Management</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Comprehensive appraisal handling with detailed performance
                  reviews, goal tracking, and development planning.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Employee Feedback</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Structured employee comment collection and feedback
                  management with real-time saving and workflow integration.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <EyeIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Performance Tracking</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Detailed performance metrics tracking with historical data,
                  progress monitoring, and actionable insights.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interface Button Layouts */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interface Button Layouts</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Header Dashboard */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Admin Tasks Dashboard</h3>

              <div className="bg-gradient-to-r from-indigo-500 to-blue-500 rounded-xl p-6 text-white">
                <div className="flex flex-col sm:flex-row items-center justify-between">
                  <div className="mb-4 sm:mb-0">
                    <h2 className="text-2xl font-bold">My Admin Tasks</h2>
                    <p className="text-indigo-100 mt-1">Manage outstanding administrative tasks</p>
                  </div>
                  <button className="inline-flex items-center gap-x-2 rounded-full bg-white px-6 py-3 text-indigo-600 font-semibold shadow-lg opacity-75 cursor-not-allowed">
                    <CheckCircleIcon className="h-5 w-5" />
                    Appraisal Outstanding
                    <span className="bg-indigo-600 text-white rounded-full px-2 py-1 text-sm font-bold">3</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Outstanding Appraisals Modal */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Outstanding Appraisals Modal</h3>

              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Outstanding Appraisals</h2>
                </div>

                <div className="p-6">
                  {/* Close Button */}
                  <div className="flex justify-end mb-4">
                    <button className="text-gray-400 hover:text-gray-700 opacity-75 cursor-not-allowed">
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Appraisal Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Appraisal:
                    </label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-2 opacity-75 cursor-not-allowed">
                      <option>Select appraisal</option>
                      <option>Jan 01, 2025 - Dec 31, 2025</option>
                      <option>Jan 01, 2024 - Dec 31, 2024</option>
                      <option>Jan 01, 2023 - Dec 31, 2023</option>
                    </select>
                  </div>

                  {/* Appraisal Information Display */}
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-bold text-indigo-900 mb-4">Appraisal Information</h3>

                    {/* Employee Comments Section */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Comments
                      </label>
                      <div className="flex gap-3">
                        <textarea
                          rows={3}
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 outline-none opacity-75 cursor-not-allowed"
                          placeholder="Enter your comments here"
                          disabled
                        />
                        <button className="inline-flex items-center rounded-full bg-indigo-600 p-3 text-white shadow hover:bg-indigo-700 opacity-75 cursor-not-allowed">
                          <PaperAirplaneIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Appraisal Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Staff Name:</span>
                          <span className="text-gray-600">John Smith</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Review Period From:</span>
                          <span className="text-gray-600">Jan 01, 2025</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Review Period To:</span>
                          <span className="text-gray-600">Dec 31, 2025</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Overall Performance:</span>
                          <span className="text-gray-600">Excellent</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Key Strengths:</span>
                          <span className="text-gray-600">Leadership, Technical Skills</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Areas for Improvement:</span>
                          <span className="text-gray-600">Project Management</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Performance Goals Met:</span>
                          <span className="text-gray-600">8 out of 10</span>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Examples Exceeding:</span>
                          <span className="text-gray-600">Led team project successfully</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Deadlines Met:</span>
                          <span className="text-gray-600">95% on time</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Leadership Skills:</span>
                          <span className="text-gray-600">Strong team player</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Peer Feedback:</span>
                          <span className="text-gray-600">Positive collaboration</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Development Goals:</span>
                          <span className="text-gray-600">Advanced certifications</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Training Courses:</span>
                          <span className="text-gray-600">Management training</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-700 w-48 mb-1 sm:mb-0">Author:</span>
                          <span className="text-gray-600">Manager Name</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-cyan-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-cyan-900">Task Dashboard</h4>
                </div>
                <p className="text-cyan-700 text-sm leading-relaxed">
                  Centralized dashboard showing all outstanding administrative tasks
                  with clear counts and quick access to task management.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Appraisal Selection</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Intuitive appraisal selection interface allowing managers to choose
                  specific review periods and access detailed performance data.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Employee Feedback</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Structured feedback collection system with real-time saving
                  and seamless integration with appraisal workflows.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Performance Details</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Comprehensive performance information display including goals,
                  achievements, areas for improvement, and development plans.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Workflow Integration</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Seamless integration with HR workflows, automatic navigation
                  to dashboard after task completion, and status tracking.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Task Prioritization</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Clear task prioritization with outstanding counts and visual
                  indicators to help managers focus on critical administrative tasks.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Admin Tasks Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-cyan-50 rounded-xl p-6 border border-cyan-200">
                  <h3 className="text-lg font-bold text-cyan-900 mb-3">Task Management</h3>
                  <ul className="space-y-2 text-sm text-cyan-700">
                    <li>• Regularly check outstanding task counts</li>
                    <li>• Prioritize urgent appraisals and reviews</li>
                    <li>• Complete feedback collection promptly</li>
                    <li>• Maintain accurate task completion records</li>
                    <li>• Follow up on overdue administrative tasks</li>
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Appraisal Handling</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Review all appraisal details thoroughly</li>
                    <li>• Provide constructive and detailed feedback</li>
                    <li>• Ensure comments are professional and objective</li>
                    <li>• Save comments immediately after writing</li>
                    <li>• Verify information accuracy before submission</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Communication</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Use clear and professional language</li>
                    <li>• Focus on specific achievements and areas</li>
                    <li>• Provide actionable development suggestions</li>
                    <li>• Maintain confidentiality of employee data</li>
                    <li>• Document all communications appropriately</li>
                  </ul>
                </div>

                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Performance Tracking</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Track completion of administrative tasks</li>
                    <li>• Monitor appraisal completion timelines</li>
                    <li>• Review feedback quality and consistency</li>
                    <li>• Identify patterns in task completion</li>
                    <li>• Report on administrative efficiency metrics</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}